import { AlignLeftOutlined } from '@ant-design/icons';
import { HardwareStateTag } from 'src/components/ven/HardwareStateTag';
import DownloadOutlined from '@ant-design/icons/DownloadOutlined';
import ExclamationCircleOutlined from '@ant-design/icons/ExclamationCircleOutlined';
import CodeFilled from '@ant-design/icons/lib/icons/CodeFilled';
import HistoryOutlined from '@ant-design/icons/lib/icons/HistoryOutlined';
import PlusCircleOutlined from '@ant-design/icons/PlusCircleOutlined';
import App from 'antd/lib/app';
import Button from 'antd/lib/button';
import Layout from 'antd/lib/layout';
import Select from 'antd/lib/select';
import Space from 'antd/lib/space';
import Tooltip from 'antd/lib/tooltip';
import Typography from 'antd/lib/typography';
import { format } from 'date-fns';
import { useContext, useMemo, useState } from 'react';
import { useLocation, useOutletContext } from 'react-router-dom';
import { CustomerVenLogsModal } from 'src/components/modals/CustomerVenLogsModal/CustomerVenLogsModal';
import { AbilityContext } from '../../../components/ability/can';
import { CustomerVenScopeInfo } from '../../../components/CustomerVenScopeInfo/CustomerVenScopeInfo';
import { AuditTrailModal } from '../../../components/modals/AuditTrailModal/AuditTrailModal';
import { PageFooter } from '../../../components/PageFooter';
import { PageHeader } from '../../../components/PageHeader';
import { SearchInput } from '../../../components/searchInput/searchInput';
import { filterCompaniesByType } from '../../../components/selectors/GlobalCompanySelector/companyHelpers';
import { ActionList } from '../../../components/table/actionList/actionList';
import { buildControlProviderFilter, getColumnFilteredValue, getColumnSortOrder } from '../../../components/table/columnFormatHelpers';
import { CustomizableColumnType, CustomizableTable } from '../../../components/table/CustomizableTable';
import { CustomizableTableContextProvider } from '../../../components/table/CustomizableTableContext';
import { WithEmptyDataTable } from '../../../components/table/withEmptyDataTable';
import { FORM_MODE, useFormMode } from '../../../components/useFormMode';
import { VenIcon } from '../../../components/VenIcon/VenIcon';
import { IView } from '../../../components/viewSwitcher/viewInterface';
import { ViewSwitcher } from '../../../components/viewSwitcher/ViewSwitcher';
import config from '../../../config';
import { AuditEntityType } from '../../../domain/audit-trail/interface';
import { DATE_FORMAT_CSV, DEFAULT_PAGINATION, PAGE_ACTION } from '../../../domain/commonConst';
import { COMPANY_TYPE } from '../../../domain/company/interface';
import { IPaginationMeta } from '../../../domain/IPagination';
import { ISite } from '../../../domain/site/interface';
import { validatePagination } from '../../../domain/validatePagination';
import { exportCustomerVens } from '../../../domain/ven';
import { ICustomerVen, IFetchVenQuery } from '../../../domain/ven/interface';
import { useCustomerVenListQuery, useDeleteCustomerVenMutation } from '../../../domain/ven/queries';
import { toUsefulParams } from '../../toUsefulParams';
import { usePageLocation } from '../../usePageState';
import { CustomerVenFormModal } from '../components/CustomerVenFormModal';
import { RemoteAccessModal } from '../components/RemoteAccessModal/RemoteAccessModal';
import { CustomerVenSidebar } from '../customerVenSidebar/CustomerVenSidebar';
import { useCustomerVenSidebar } from '../customerVenSidebar/useCustomerVenSidebar';
import { venStatusFilter, viewOptions } from './venViewInterface';
import { SidebarGrid } from 'src/components/Sidebar/SidebarGrid';

export const CustomerVenView = () => {
    const { notification, modal } = App.useApp();
    const ability = useContext(AbilityContext);
    const location = useLocation();
    const { setPageQuery, queryToState } = usePageLocation();
    const pageState: any = queryToState(location.search);

    const [hardwareCustomerVen, setHardwareCustomerVen] = useState<ICustomerVen | Partial<ICustomerVen>>();
    const [customerVen, setCustomerVen] = useState<ICustomerVen | Partial<ICustomerVen>>();
    const [exportLoading, setExportLoading] = useState<boolean>(false);
    const [venAction, setVenAction] = useState<PAGE_ACTION | ''>();

    const { mutateAsync: deleteCustomerVen } = useDeleteCustomerVenMutation();
    const { isFiltersSidebarEnabled, setSidebarCollapsed, sidebarCollapsed, layoutPaddingLeft } = useCustomerVenSidebar();
    const { onSetFormMode, isEditMode, formMode } = useFormMode();
    const { company, view, handleViewMode, companies, auth }: IView = useOutletContext();
    const isControlProvider = auth?.user?.isControlProvider()!;
    const controlProviderCompanies = useMemo(() => filterCompaniesByType(companies!, COMPANY_TYPE.CONTROL_PROVIDER), [companies]);

    const params = toUsefulParams(pageState);

    const listQuery: IFetchVenQuery = {
        hideRelatedToClosedSites: true,
        pagination: params.pagination ?? DEFAULT_PAGINATION,
        ...(params?.filter && { filter: params?.filter }),
        ...(params?.sorter && { sorter: params?.sorter }),
        ...(params?.search && { search: params?.search }),
        company_id: params?.company_id ?? null,
        include: 'control_provider,company,site',
    };

    const {
        data,
        isFetching,
        refetch: refetchCustomerVens,
    } = useCustomerVenListQuery(listQuery, {
        keepPreviousData: true,
        refetchInterval: 60_000,
    });
    const customerVens = data?.data || [];
    const meta = data?.meta || ({ total: 0 } as IPaginationMeta);

    async function exportCustomerVensAction() {
        try {
            setExportLoading(true);
            const currentCompany = companies!.find(c => c.company_id === company);
            const companyName = currentCompany?.company_name || '';
            const fileName = `${companyName}-customerVens--${format(new Date(), DATE_FORMAT_CSV)}.csv`;

            await exportCustomerVens(toUsefulParams(pageState), fileName);
        } catch (err: any) {
            notification.error({
                key: 'customer-ven-export-error',
                message: err.message || 'Cannot export customer VENs!',
            });
        }
        setExportLoading(false);
    }

    const handleFieldChange = (field: string) => (value: any) => {
        setPageQuery({
            ...pageState,
            filter: {
                ...pageState.filter,
                [field]: value,
            },
        });
    };

    const handleSearch = (value: string) => {
        if (value === pageState.search) {
            setPageQuery({
                ...pageState,
                pagination: { ...pageState.pagination, current: DEFAULT_PAGINATION.current },
            });
            return;
        }

        setPageQuery({ ...pageState, search: value });
    };

    async function onTableChange(pagination: any, filter: any, sorter: any) {
        const preparedParams = toUsefulParams({
            pagination,
            sorter,
            filter: {
                ...pageState.filter,
                ...filter,
            },
        });

        setPageQuery({
            ...pageState,
            ...preparedParams,
        });
    }

    function deleteCustomerVenWithConfirm(selectedVen: ICustomerVen) {
        return () => {
            modal.confirm({
                icon: <ExclamationCircleOutlined />,
                content: `Deleting customer VEN ${selectedVen.ven_username || ''}. Are you sure?`,
                onOk: async () => {
                    await deleteCustomerVenAction(selectedVen);
                },
                onCancel() {
                    console.log('Cancel');
                },
            });
        };
    }

    async function deleteCustomerVenAction(selectedVen: ICustomerVen) {
        try {
            await deleteCustomerVen(selectedVen);

            const validPagination = validatePagination(pageState.pagination, meta.total - 1);

            setPageQuery({ ...pageState, pagination: validPagination });
            await refetchCustomerVens({ ...pageState, pagination: validPagination });

            notification.info({ key: 'customer-ven-delete-info', message: 'Customer VEN deleted' });
        } catch (err: any) {
            notification.error({ key: 'customer-ven-delete-error', message: err.message || 'Cannot delete VEN!' });
        }
    }

    const showNewVenModal = () => {
        onSetFormMode(FORM_MODE.NEW);

        setCustomerVen({
            company_id: !!auth?.user?.isCustomer() ? auth?.user?.company_id : undefined,
            site_id: undefined,
            ven_username: undefined,
            ven_password: undefined,
            ven_id: undefined,
            ven_uri: `${config.HOST}/api/vtn`,
            ven_vtn_id: 'ENERSPONSE_VTN',
            ven_cert: undefined,
            ven_default_event_signal: undefined,
            ven_key: undefined,
            ven_market_context: 'enersponse',
            ven_profile: '2.0b',
            ven_registration_id: undefined,
            mac_address: undefined,
        });
    };

    function showEditVenModal(record: ICustomerVen) {
        return () => {
            onSetFormMode(FORM_MODE.EDIT);
            const siteWithCompany = { ...record.site, company: record.company };
            setCustomerVen({ ...record, site: siteWithCompany as ISite });
        };
    }

    async function onVenModalClose(selectedVen: ICustomerVen) {
        setCustomerVen(undefined);
        onSetFormMode('');

        if (selectedVen) {
            await refetchCustomerVens(toUsefulParams({ ...pageState }));
        }
    }

    function showRemoteAccessModal(record: ICustomerVen) {
        return () => {
            setHardwareCustomerVen(record);
        };
    }

    async function onRemoteAccessModalClose(selectedVen: ICustomerVen) {
        setHardwareCustomerVen(undefined);
    }

    function showAuditTrailModal(record: ICustomerVen) {
        return () => {
            setVenAction(PAGE_ACTION.AUDIT_TRAIL);
            setCustomerVen(record);
        };
    }

    function showLogsModal(record: ICustomerVen) {
        return () => {
            setVenAction(PAGE_ACTION.SHOW_LOGS);
            setCustomerVen(record);
        };
    }

    function getActions(record: ICustomerVen) {
        const actions = [];

        if (ability.can('update', 'CustomerVen')) {
            actions.push({
                title: 'Edit',
                onClick: (record: ICustomerVen) => showEditVenModal(record),
            });
        }

        if (ability.can('delete', 'CustomerVen')) {
            actions.push({
                title: 'Delete',
                onClick: (record: ICustomerVen) => deleteCustomerVenWithConfirm(record),
            });
        }

        if (record.mac_address && ability.can('read', 'RemoteAccess')) {
            actions.push({
                title: 'Remote Access',
                icon: <CodeFilled />,
                onClick: (record: ICustomerVen) => showRemoteAccessModal(record),
            });
        }

        if (auth?.user?.isAdminRoleType()) {
            actions.push({
                title: 'Show Logs',
                icon: <AlignLeftOutlined />,
                onClick: (record: ICustomerVen) => showLogsModal(record),
            });
        }

        if (auth?.user?.isAdminRoleType()) {
            actions.push({
                title: 'Audit Trail',
                onClick: (record: ICustomerVen) => showAuditTrailModal(record),
                icon: <HistoryOutlined />,
            });
        }

        return actions;
    }

    const customerVenColumns: CustomizableColumnType<ICustomerVen>[] = [
        {
            key: 'ven_icon',
            title: 'Status',
            width: 70,
            render: (type, record) => {
                return <VenIcon ven={record} controlProvider={record.control_provider} />;
            },
        },
        {
            key: 'ven_username',
            title: 'Username',
            dataIndex: 'ven_username',
            width: 100,
            sorter: (a, b) => a.ven_username.localeCompare(b.ven_username),
            ...getColumnSortOrder('ven_username', pageState.sorter),
        },
        {
            key: 'ven_id',
            title: 'VEN ID',
            dataIndex: 'ven_id',
            width: 100,
            sorter: (a, b) => a.ven_id.localeCompare(b.ven_id),
            ...getColumnSortOrder('ven_id', pageState.sorter),
        },
        {
            key: 'ven_vtn_id',
            title: 'VTN ID',
            dataIndex: 'ven_vtn_id',
            width: 100,
            sorter: (a, b) => a.ven_vtn_id.localeCompare(b.ven_vtn_id),
            ...getColumnSortOrder('ven_vtn_id', pageState.sorter),
        },
        {
            key: 'control_providers_id',
            title: 'Company / Scope',
            width: 200,
            render: (type, record) => <CustomerVenScopeInfo customerVen={record} />,
            filterSearch: (input, record) => !!record?.text?.toString()?.toLowerCase().includes(input.toLowerCase()),
            ...(!isFiltersSidebarEnabled && !isControlProvider
                ? {
                      filterMultiple: true,
                      filters: buildControlProviderFilter(controlProviderCompanies, { includeWithoutControlProvider: true }),
                      ...getColumnFilteredValue('control_providers_id', pageState.filter),
                  }
                : { filtered: false }),
        },
        ...(auth?.user?.isAdminRoleType()
            ? ([
                  {
                      key: 'control_provider',
                      title: 'Site Control Provider',
                      width: 160,
                      render: (_: any, record: ICustomerVen) => record?.site?.control_provider?.company_name ?? 'Without Provider',
                  },
              ] as CustomizableColumnType<ICustomerVen>[])
            : []),
        ...(auth?.user?.isAdminRoleType()
            ? ([
                  {
                      key: 'hardware_version',
                      title: 'Hardware Version',
                      width: 130,
                      hidden: true,
                      render: (_: any, record: ICustomerVen) => record.hardware_state?.version ?? '',
                  },
                  {
                      key: 'hardware_status',
                      title: 'Hardware State',
                      width: 130,
                      hidden: true,
                      render: (_: any, record: ICustomerVen) => <HardwareStateTag hardwareState={record.hardware_state} />,
                  },
              ] as CustomizableColumnType<ICustomerVen>[])
            : []),
        {
            key: 'action',
            sorter: false,
            width: 50,
            render: (text: string, record) => <ActionList actions={getActions(record)} item={record} />,
        },
    ];

    return (
        <CustomizableTableContextProvider configKey="customerVen" columns={customerVenColumns}>
            <Layout style={{ height: '100%', paddingLeft: layoutPaddingLeft }}>
                {isFiltersSidebarEnabled && (
                    <CustomerVenSidebar
                        collapsed={sidebarCollapsed}
                        onCollapseChange={collapsed => setSidebarCollapsed(collapsed)}
                        onFilterChange={() => {}}
                        gridComponent={<SidebarGrid />}
                        totalRecords={meta.total}
                    />
                )}

                <Layout.Content style={{ padding: '0 26px' }}>
                    <PageHeader
                        pageTitle="VEN"
                        extra={[
                            <ViewSwitcher viewOptions={viewOptions(ability!)} key="ven-view-switcher" view={view} handleViewMode={handleViewMode} />,
                        ]}
                        actions={[
                            !isFiltersSidebarEnabled && (
                                <Space key="customer-ven-type-filter">
                                    <Typography.Text>VEN Type:</Typography.Text>
                                    <Select
                                        size="large"
                                        value={pageState?.filter?.type ?? ''}
                                        onChange={handleFieldChange('type')}
                                        style={{ width: '145px' }}
                                        data-cy="ven-type-selector"
                                        options={[
                                            {
                                                label: 'All',
                                                value: '',
                                            },
                                            {
                                                label: 'Customer VEN',
                                                value: 'customer_ven',
                                            },
                                            {
                                                label: 'Enersponse GEM',
                                                value: 'hardware',
                                            },
                                        ]}
                                    />
                                </Space>
                            ),
                            !isFiltersSidebarEnabled && (
                                <Space key="customer-ven-filter">
                                    <Typography.Text>VEN status:</Typography.Text>
                                    <Select
                                        size="large"
                                        value={pageState?.filter?.status ?? ''}
                                        onChange={handleFieldChange('status')}
                                        style={{ width: '110px' }}
                                        data-cy="ven-status-selector"
                                        defaultValue={''}
                                        options={[
                                            {
                                                label: 'All',
                                                value: '',
                                            },
                                            ...venStatusFilter,
                                        ]}
                                    />
                                </Space>
                            ),
                            <SearchInput key="customer-ven-search" onSearch={handleSearch} defaultValue={pageState?.search} />,
                            <Tooltip key="download-csv" title={company ? '' : 'Please, select company first'}>
                                <span>
                                    <Button
                                        size="large"
                                        onClick={exportCustomerVensAction}
                                        icon={<DownloadOutlined />}
                                        loading={exportLoading}
                                        disabled={!company}
                                        data-cy="csv-download"
                                    >
                                        Download CSV
                                    </Button>
                                </span>
                            </Tooltip>,
                            ability.can('create', 'CustomerVen') && (
                                <Button
                                    size="large"
                                    key="new-customer-ven"
                                    type="primary"
                                    onClick={showNewVenModal}
                                    icon={<PlusCircleOutlined />}
                                    data-cy="create-customer-ven"
                                >
                                    New VEN
                                </Button>
                            ),
                        ]}
                    />

                    <WithEmptyDataTable condition={isFetching}>
                        <CustomizableTable<ICustomerVen>
                            size="small"
                            rowKey="id"
                            className="customer-ven-table"
                            showHiddenColumnsCount={auth?.user?.isAdminRoleType()}
                            sticky={{ offsetHeader: 62 }}
                            dataSource={customerVens}
                            loading={isFetching}
                            onChange={onTableChange}
                            meta={meta}
                        />
                    </WithEmptyDataTable>

                    <PageFooter />
                </Layout.Content>
            </Layout>

            {customerVen && (formMode === FORM_MODE.EDIT || formMode === FORM_MODE.NEW) && (
                <CustomerVenFormModal ven={customerVen} onClose={onVenModalClose} isEditMode={isEditMode} />
            )}

            {hardwareCustomerVen && <RemoteAccessModal ven={hardwareCustomerVen} onClose={onRemoteAccessModalClose} />}

            {customerVen && venAction === PAGE_ACTION.AUDIT_TRAIL && (
                <AuditTrailModal onClose={() => setVenAction('')} entityId={customerVen.id!} entityType={AuditEntityType.CUSTOMER_VEN} />
            )}

            {customerVen && venAction === PAGE_ACTION.SHOW_LOGS && <CustomerVenLogsModal onClose={() => setVenAction('')} ven={customerVen} />}
        </CustomizableTableContextProvider>
    );
};
